<template>
  <Teleport to="body">
    <!-- Overlay -->
    <Transition name="overlay-fade">
      <div
        v-if="isOpen"
        class="mini-cart-overlay"
        @click="$emit('close')"
        aria-hidden="true"
      ></div>
    </Transition>

    <!-- Drawer -->
    <Transition name="drawer-slide">
      <div
        v-if="isOpen"
        class="mini-cart-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Tu carrito"
        tabindex="-1"
        ref="drawerRef"
      >
        <!-- Header -->
        <div class="mini-cart-header">
          <h5 class="mini-cart-title">Tu carrito</h5>
          <button
            class="mini-cart-close"
            @click="$emit('close')"
            aria-label="Cerrar carrito"
            type="button"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>

        <!-- Items list -->
        <div class="mini-cart-body" v-if="cartItems.length > 0">
          <div
            v-for="item in cartItems"
            :key="item.lineId"
            class="mini-cart-item"
          >
            <!-- Thumbnail -->
            <div class="mini-cart-item-image">
              <img
                v-if="item.image"
                :src="item.image"
                :alt="item.name"
                class="item-thumb"
              />
              <div v-else class="item-thumb-placeholder">
                <i class="fas fa-box"></i>
              </div>
            </div>

            <!-- Info -->
            <div class="mini-cart-item-info">
              <p class="item-name" :title="item.name">{{ item.name }}</p>

              <!-- Variants -->
              <div
                v-if="item.variants && Object.keys(item.variants).length"
                class="item-variants"
              >
                <span
                  v-for="(opt, vname) in item.variants"
                  :key="vname"
                  class="item-variant-badge"
                >
                  {{ vname }}: {{ opt }}
                </span>
              </div>

              <!-- Prices -->
              <div class="item-prices">
                <span
                  v-if="item.total_discount > 0 && item.original_price"
                  class="item-original-price"
                >
                  {{ formatPrice(item.original_price, item.currency) }}
                </span>
                <span class="item-price">
                  {{ formatPrice(item.price, item.currency) }}
                </span>
                <span v-if="item.total_discount > 0" class="item-discount-badge">
                  {{ Math.round(item.total_discount) }}% OFF
                </span>
              </div>

              <!-- Controls row -->
              <div class="item-controls">
                <div class="quantity-controls">
                  <button
                    class="qty-btn"
                    type="button"
                    aria-label="Disminuir cantidad"
                    @click="decrease(item)"
                  >
                    <i class="fas fa-minus"></i>
                  </button>
                  <span class="qty-value">{{ item.quantity }}</span>
                  <button
                    class="qty-btn"
                    type="button"
                    aria-label="Aumentar cantidad"
                    @click="increase(item)"
                  >
                    <i class="fas fa-plus"></i>
                  </button>
                </div>

                <span class="item-subtotal">
                  {{ formatPrice(item.price * item.quantity, item.currency) }}
                </span>

                <button
                  class="remove-btn"
                  type="button"
                  aria-label="Eliminar producto"
                  @click="remove(item.lineId)"
                >
                  <i class="fas fa-trash-alt"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty state -->
        <div v-else class="mini-cart-empty">
          <i class="fas fa-shopping-bag empty-icon"></i>
          <p class="empty-title">Tu carrito está vacío</p>
          <p class="empty-sub">Explorá nuestros productos y encontrá lo que buscás.</p>
          <a
            href="/productos"
            class="btn btn-outline-secondary btn-sm mt-3"
            @click="$emit('close')"
          >
            Ver productos
          </a>
        </div>

        <!-- Footer -->
        <div v-if="cartItems.length > 0" class="mini-cart-footer">
          <div class="mini-cart-subtotal">
            <span class="subtotal-label">Subtotal</span>
            <span class="subtotal-value">{{ formatCartTotal }}</span>
          </div>
          <router-link
            to="/carrito"
            class="btn-checkout"
            @click="$emit('close')"
          >
            Finalizar compra
          </router-link>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { computed, ref, watch, onMounted, onUnmounted, nextTick, defineProps, defineEmits } from 'vue';
import { useStore } from 'vuex';

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close']);

const store = useStore();
const drawerRef = ref(null);

const cartItems = computed(() => store.getters.cartItems);

const formatPrice = (price, currency) => {
  if (currency === 'USD') {
    return `USD ${Number(price).toFixed(2)}`;
  }
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'ARS',
  }).format(price);
};

const formatCartTotal = computed(() => {
  const arsItems = cartItems.value.filter((i) => i.currency !== 'USD');
  const usdItems = cartItems.value.filter((i) => i.currency === 'USD');

  const arsTotal = arsItems.reduce((sum, i) => sum + i.price * i.quantity, 0);
  const usdTotal = usdItems.reduce((sum, i) => sum + i.price * i.quantity, 0);

  if (usdTotal > 0 && arsTotal > 0) {
    return `${formatPrice(arsTotal, 'ARS')} + USD ${usdTotal.toFixed(2)}`;
  } else if (usdTotal > 0) {
    return `USD ${usdTotal.toFixed(2)}`;
  }
  return formatPrice(arsTotal, 'ARS');
});

const increase = (item) => {
  store.dispatch('updateQuantity', { lineId: item.lineId, quantity: item.quantity + 1 });
};

const decrease = (item) => {
  if (item.quantity <= 1) {
    store.dispatch('removeFromCart', item.lineId);
  } else {
    store.dispatch('updateQuantity', { lineId: item.lineId, quantity: item.quantity - 1 });
  }
};

const remove = (lineId) => {
  store.dispatch('removeFromCart', lineId);
};

const handleKeydown = (e) => {
  if (e.key === 'Escape' && props.isOpen) emit('close');
};

onMounted(() => {
  window.addEventListener('keydown', handleKeydown);
});

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown);
});

watch(
  () => props.isOpen,
  async (val) => {
    if (val) {
      document.body.style.overflow = 'hidden';
      await nextTick();
      drawerRef.value?.focus();
    } else {
      document.body.style.overflow = '';
    }
  }
);
</script>

<style scoped>
/* ── Overlay ──────────────────────────────────────────── */
.mini-cart-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1305;
}

/* ── Drawer panel ─────────────────────────────────────── */
.mini-cart-drawer {
  position: fixed;
  top: 0;
  right: 0;
  width: 400px;
  max-width: 100%;
  height: 100%;
  background: #141414;
  z-index: 1306;
  display: flex;
  flex-direction: column;
  box-shadow: -4px 0 28px rgba(0, 0, 0, 0.45);
  outline: none;
}

/* ── Transitions ──────────────────────────────────────── */
.overlay-fade-enter-active,
.overlay-fade-leave-active {
  transition: opacity 0.25s ease;
}
.overlay-fade-enter-from,
.overlay-fade-leave-to {
  opacity: 0;
}

.drawer-slide-enter-active,
.drawer-slide-leave-active {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.drawer-slide-enter-from,
.drawer-slide-leave-to {
  transform: translateX(100%);
}

/* ── Header ───────────────────────────────────────────── */
.mini-cart-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  flex-shrink: 0;
}

.mini-cart-title {
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0;
  color: #fff;
}

.mini-cart-close {
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 1rem;
  color: #bdbdbd;
  padding: 0.35rem;
  line-height: 1;
  border-radius: 4px;
  transition: background 0.15s, color 0.15s;
}

.mini-cart-close:hover {
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
}

/* ── Body (scrollable) ────────────────────────────────── */
.mini-cart-body {
  flex: 1;
  overflow-y: auto;
  padding: 0.25rem 0;
}

/* ── Item ─────────────────────────────────────────────── */
.mini-cart-item {
  display: flex;
  gap: 0.875rem;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.mini-cart-item:last-child {
  border-bottom: none;
}

.mini-cart-item-image {
  flex-shrink: 0;
  width: 68px;
  height: 68px;
  border-radius: 8px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.06);
  display: flex;
  align-items: center;
  justify-content: center;
}

.item-thumb {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.item-thumb-placeholder {
  color: #777;
  font-size: 1.4rem;
}

.mini-cart-item-info {
  flex: 1;
  min-width: 0;
}

.item-name {
  font-size: 0.875rem;
  font-weight: 500;
  color: #fff;
  margin: 0 0 0.25rem;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.item-variants {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  margin-bottom: 0.35rem;
}

.item-variant-badge {
  font-size: 0.7rem;
  color: #d5d5d5;
  background: rgba(255, 255, 255, 0.08);
  padding: 1px 6px;
  border-radius: 3px;
}

.item-prices {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-bottom: 0.5rem;
  flex-wrap: wrap;
}

.item-original-price {
  font-size: 0.75rem;
  color: #8b8b8b;
  text-decoration: line-through;
}

.item-price {
  font-size: 0.875rem;
  font-weight: 600;
  color: #fff;
}

.item-discount-badge {
  font-size: 0.68rem;
  background: rgba(239, 68, 68, 0.18);
  color: #fca5a5;
  padding: 1px 5px;
  border-radius: 3px;
  font-weight: 600;
}

/* ── Controls ─────────────────────────────────────────── */
.item-controls {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.quantity-controls {
  display: flex;
  align-items: center;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 6px;
  overflow: hidden;
}

.qty-btn {
  background: rgba(255, 255, 255, 0.04);
  border: none;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #d0d0d0;
  font-size: 0.65rem;
  transition: background 0.15s;
}

.qty-btn:hover {
  background: rgba(255, 255, 255, 0.1);
}

.qty-value {
  width: 28px;
  text-align: center;
  font-size: 0.85rem;
  font-weight: 500;
  color: #fff;
  border-left: 1px solid rgba(255, 255, 255, 0.12);
  border-right: 1px solid rgba(255, 255, 255, 0.12);
  line-height: 28px;
}

.item-subtotal {
  font-size: 0.82rem;
  font-weight: 600;
  color: #fff;
  margin-left: auto;
  white-space: nowrap;
}

.remove-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  color: #8a8a8a;
  font-size: 0.82rem;
  padding: 0.2rem;
  transition: color 0.2s;
  line-height: 1;
}

.remove-btn:hover {
  color: var(--primary-color);
}

/* ── Empty state ──────────────────────────────────────── */
.mini-cart-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  text-align: center;
}

.empty-icon {
  font-size: 3rem;
  color: #555;
  margin-bottom: 1rem;
}

.empty-title {
  font-size: 1rem;
  font-weight: 600;
  color: #fff !important;
  margin: 0 0 0.35rem;
}

.empty-sub {
  font-size: 0.85rem;
  color: #bdbdbd !important;
  margin: 0;
}

/* ── Footer ───────────────────────────────────────────── */
.mini-cart-footer {
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  padding: 1.25rem 1.5rem;
  flex-shrink: 0;
  background: #141414;
}

.mini-cart-subtotal {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.subtotal-label {
  font-size: 0.9rem;
  color: #bdbdbd;
}

.subtotal-value {
  font-size: 1rem;
  font-weight: 700;
  color: #fff;
}

.btn-checkout {
  display: block;
  width: 100%;
  background-color: var(--primary-color);
  color: #fff !important;
  border: none;
  padding: 0.78rem 1rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.925rem;
  text-decoration: none;
  text-align: center;
  transition: background 0.2s, transform 0.1s;
  cursor: pointer;
}

.btn-checkout:hover {
  background-color: var(--primary-dark);
}

.btn-checkout:active {
  transform: scale(0.98);
}

/* ── Responsive ───────────────────────────────────────── */
@media (max-width: 768px) {
  .mini-cart-drawer {
    width: 85%;
  }
}

@media (max-width: 400px) {
  .mini-cart-drawer {
    width: 100%;
  }
}
</style>
